// Selection = a set of node `path` prefixes. A bare prefix includes its subtree; a
// "!" prefix excludes one from an including ancestor; for any page the longest match
// wins. Mirrors `selected_urls` in the backend `url_tree.py`.

export interface UrlTreeNode {
  label: string;
  path: string;
  url: string | null;
  count: number;
  children: UrlTreeNode[];
  truncated: number;
}

const isExclude = (entry: string): boolean => entry.startsWith('!');
const stripExclude = (entry: string): string => (entry.startsWith('!') ? entry.slice(1) : entry);

// True when `descendant` lies strictly under `ancestor`; the empty-path root is the
// ancestor of every node (sibling hosts share no prefix, so only it can group them).
const isUnder = (descendant: string, ancestor: string): boolean =>
  ancestor === '' ? descendant !== '' : descendant.startsWith(`${ancestor}/`);

type Coverage = 'include' | 'exclude' | 'none';

// Sign of the longest prefix covering `path`; a same-length exclude beats an include.
const coverage = (path: string, selection: Set<string>): Coverage => {
  let bestLen = -1;
  let sign: Coverage = 'none';
  selection.forEach((entry) => {
    const prefix = stripExclude(entry);
    if (!(prefix === path || isUnder(path, prefix))) return;
    const excl = isExclude(entry);
    if (prefix.length > bestLen || (prefix.length === bestLen && excl)) {
      bestLen = prefix.length;
      sign = excl ? 'exclude' : 'include';
    }
  });
  return sign;
};

// Whether any include (or exclude) entry sits strictly under `path`.
const hasDescendant = (
  path: string,
  selection: Set<string>,
  sign: 'include' | 'exclude',
): boolean =>
  Array.from(selection).some((entry) => {
    const prefix = stripExclude(entry);
    return (sign === 'exclude') === isExclude(entry) && prefix !== path && isUnder(prefix, path);
  });

// Fully selected: covered by an include with nothing excluded beneath it.
export const isChecked = (path: string, selection: Set<string>): boolean =>
  coverage(path, selection) === 'include' && !hasDescendant(path, selection, 'exclude');

// Partially selected: covered by an include but with exclusions beneath, or not
// covered but with inclusions beneath.
export const isIndeterminate = (path: string, selection: Set<string>): boolean =>
  coverage(path, selection) === 'include'
    ? hasDescendant(path, selection, 'exclude')
    : hasDescendant(path, selection, 'include');

// Toggle the subtree at `path`, adding only the minimal marker against the nearest
// covering ancestor, so truncated / lazily-loaded children need no enumeration.
export const toggleSelection = (selection: Set<string>, path: string): Set<string> => {
  const next = new Set(selection);
  const turningOn = !isChecked(path, selection);
  // Drop any entries at or under `path`; we're redefining this subtree.
  Array.from(next).forEach((entry) => {
    const prefix = stripExclude(entry);
    if (prefix === path || isUnder(prefix, path)) next.delete(entry);
  });
  const ancestor = coverage(path, next); // nearest covering ancestor, post-clear
  if (turningOn) {
    if (ancestor !== 'include') next.add(path);
  } else if (ancestor === 'include') {
    next.add(`!${path}`);
  }
  return next;
};

// Graft lazily-loaded children onto the node at `path` (immutable, idempotent),
// shrinking `truncated` so "+N more" and `countSelected` see the loaded pages.
export const mergeChildren = (
  node: UrlTreeNode,
  path: string,
  batch: UrlTreeNode[],
): UrlTreeNode => {
  if (node.path === path) {
    const existing = new Set((node.children ?? []).map((child) => child.path));
    const fresh = batch.filter((child) => !existing.has(child.path));
    if (fresh.length === 0) return node;
    return {
      ...node,
      children: [...(node.children ?? []), ...fresh],
      truncated: Math.max(0, node.truncated - fresh.length),
    };
  }
  if (!isUnder(path, node.path)) return node;
  let changed = false;
  const children = (node.children ?? []).map((child) => {
    const merged = mergeChildren(child, path, batch);
    if (merged !== child) changed = true;
    return merged;
  });
  return changed ? { ...node, children } : node;
};

// Display domain for the tree root: the host (single-host) or seed-named label
// (multi-host), falling back to the child hosts' shared domain for legacy maps.
export const rootDomain = (node: UrlTreeNode): string => {
  if (node.path) return node.path;
  if (node.label) return node.label;
  const hosts = (node.children ?? []).map((child) => child.label).filter(Boolean);
  if (hosts.length === 0) return node.label;
  const reversed = hosts.map((host) => host.split('.').reverse());
  const [first] = reversed;
  let shared = 0;
  while (shared < first.length && reversed.every((labels) => labels[shared] === first[shared])) {
    shared += 1;
  }
  return shared === 0 ? hosts[0] : first.slice(0, shared).reverse().join('.');
};

// Pages the selection covers, honouring excludes; settles on a subtree's full `count`
// (which includes truncated descendants) so it stays correct on an incomplete tree.
export const countSelected = (
  node: UrlTreeNode,
  selection: Set<string>,
  inherited = false,
): number => {
  let selected = inherited;
  if (selection.has(node.path)) selected = true;
  if (selection.has(`!${node.path}`)) selected = false;

  const hasDeeper = Array.from(selection).some((entry) => isUnder(stripExclude(entry), node.path));
  if (!hasDeeper) return selected ? node.count : 0;

  const children = node.children ?? [];
  let shownLeaves = 0;
  let sum = 0;
  children.forEach((child) => {
    sum += countSelected(child, selection, selected);
    shownLeaves += child.count;
  });
  const selfLeaf = node.url != null ? 1 : 0;
  const truncatedLeaves = Math.max(0, node.count - shownLeaves - selfLeaf);
  if (selected) sum += truncatedLeaves + selfLeaf;
  return sum;
};

// Clear the whole selection ("select none").
export const clearSelection = (): Set<string> => new Set<string>();

// Pages currently selected under `node`, seeded from the coverage an ancestor include gives it.
const selectedUnder = (node: UrlTreeNode, selection: Set<string>): number =>
  countSelected(node, selection, coverage(node.path, selection) === 'include');

// Copy every marker under a subtree taken whole, reproducing the user's coverage exactly: carrying
// only the excludes would drop the re-includes they shadow, carrying none would resurrect deselections.
const carrySubtreeMarkers = (
  node: UrlTreeNode,
  result: Set<string>,
  current: Set<string>,
): void => {
  current.forEach((entry) => {
    if (isUnder(stripExclude(entry), node.path)) result.add(entry);
  });
};

// Keep the first `budget` pages of `node` that are *already selected*, in tree order.
// `covered` means an include placed above already covers this subtree.
const keepFirst = (
  node: UrlTreeNode,
  budget: number,
  covered: boolean,
  result: Set<string>,
  current: Set<string>,
): number => {
  const selected = selectedUnder(node, current);
  const drop = () => {
    if (covered) result.add(`!${node.path}`);
  };
  if (selected === 0 || budget <= 0) {
    drop();
    return 0;
  }

  // Only blanket-include a node the user actually has selected at this level; when their
  // pages come from a deeper include instead, including here would add pages they never chose.
  const included = coverage(node.path, current) === 'include';
  if (budget >= selected && included) {
    if (!covered) result.add(node.path);
    carrySubtreeMarkers(node, result, current);
    return selected;
  }

  const children = node.children ?? [];
  const selfPage = node.url != null && included ? 1 : 0;
  const shownSelected = children.reduce((sum, child) => sum + selectedUnder(child, current), 0);

  if (included && selected === selfPage + shownSelected) {
    if (!covered) result.add(node.path);
    let taken = selfPage;
    children.forEach((child) => {
      taken += keepFirst(child, budget - taken, true, result, current);
    });
    return taken;
  }

  // Selected pages the display caps hid can't be excluded one by one, so this subtree is
  // all-or-nothing and did not fit: drop it and carve its shown children instead.
  drop();
  let taken = 0;
  children.forEach((child) => {
    taken += keepFirst(child, budget - taken, false, result, current);
  });
  return taken;
};

// Reduce `current` to at most `limit` pages, keeping the earliest in tree order. Only ever
// removes pages, so it can never reverse a deselection the user made.
export const trimToLimit = (
  node: UrlTreeNode,
  limit: number,
  current: Set<string>,
): Set<string> => {
  const result = new Set<string>();
  keepFirst(node, limit, false, result, current);
  return result;
};
